// Default part-catalog search URLs — soft-coded starting points; editable
// in Settings. Use {q} where the URL-encoded search term goes.
import { shortId } from './job-helpers.js';

export const DEFAULT_PART_SEARCH_LINKS = [
  {
    id: 'napa',
    label: 'NAPA',
    // referer=v2 is what NAPA's site uses for live search; sitelink redirects to homepage.
    url: 'https://www.napaonline.com/en/search?text={q}&referer=v2',
  },
  {
    id: 'oreilly',
    label: "O'Reilly",
    url: 'https://www.oreillyauto.com/search?q={q}',
  },
  {
    id: 'autozone',
    label: 'AutoZone',
    url: 'https://www.autozone.com/searchresult?searchText={q}',
  },
  {
    id: 'rockauto',
    label: 'RockAuto',
    // partsearch= is ignored; partnum= runs the catalog lookup (part # or keywords)
    url: 'https://www.rockauto.com/en/partsearch/?partnum={q}',
  },
];

export function isValidPartSearchUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (!url.includes('{q}')) return false;
  try {
    const probe = url.replace(/\{q\}/g, 'test');
    const u = new URL(probe);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

// Upgrade URLs saved from earlier builds so existing Settings profiles pick up fixes.
export function patchPartSearchLink(link) {
  if (!link?.url) return link;
  let url = link.url;

  if (url.includes('partsearch={q}')) {
    url = url.replace('partsearch={q}', 'partnum={q}');
  }

  if (url.includes('napaonline.com') && url.includes('text={q}')) {
    if (!url.includes('referer=')) {
      url = `${url}&referer=v2`;
    } else {
      url = url.replace('referer=sitelink', 'referer=v2');
    }
  }

  return url === link.url ? link : { ...link, url };
}

export function normalizePartSearchLinks(links) {
  return (links || [])
    .map((l) => ({
      id: l.id || shortId(),
      label: (l.label || '').trim(),
      url: (l.url || '').trim(),
    }))
    .filter((l) => l.label && isValidPartSearchUrl(l.url))
    .map(patchPartSearchLink);
}

export function resolvePartSearchLinks(links) {
  const normalized = normalizePartSearchLinks(links);
  if (normalized.length > 0) return normalized;
  return DEFAULT_PART_SEARCH_LINKS.map((l) => ({ ...l }));
}

function encodeSearchQuery(urlTemplate, query) {
  const encoded = encodeURIComponent(query);
  // NAPA and RockAuto use + for spaces in their own search URLs.
  if (urlTemplate.includes('napaonline.com') && urlTemplate.includes('text=')) {
    return encoded.replace(/%20/g, '+');
  }
  if (urlTemplate.includes('rockauto.com') && urlTemplate.includes('partnum=')) {
    return encoded.replace(/%20/g, '+');
  }
  return encoded;
}

export function buildPartSearchUrl(urlTemplate, query, { vehicleLabel = '', includeVehicle = false } = {}) {
  const trimmed = String(query || '').trim();
  if (!trimmed || !urlTemplate?.includes('{q}')) return null;
  let q = trimmed;
  if (includeVehicle && vehicleLabel?.trim()) {
    q = `${vehicleLabel.trim()} ${trimmed}`;
  }
  const template = patchPartSearchLink({ url: urlTemplate }).url;
  return template.replace(/\{q\}/g, encodeSearchQuery(template, q));
}

export function openPartSearch(urlTemplate, query, options) {
  const url = buildPartSearchUrl(urlTemplate, query, options);
  if (!url) return false;
  openPartSearchUrl(url);
  return true;
}

// Anchor click survives mobile Safari / PWA popup rules better than window.open.
export function openPartSearchUrl(url) {
  if (!url) return false;
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}

export function openAllPartSearchUrls(links, query, options) {
  const urls = (links || [])
    .map((link) => buildPartSearchUrl(link.url, query, options))
    .filter(Boolean);
  urls.forEach((url) => openPartSearchUrl(url));
  return urls.length;
}
